// React
import React from 'react';
import PropTypes from 'prop-types';
import { css } from '@emotion/core';

import CybsTextField from '@cybs-ui/components/lib/textfield';
import CybsScreenReaderManager from '@cybs-ui/components/lib/screen-reader-manager';

class FilterInput extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      value: props.value
    };

    // delays call to update parent function
    const debounce = (func, wait, immediate) => {
      let timeout;
      // eslint-disable-next-line
      return function () {
        // changing to arrow functions changes context..
        const context = this;
        const args = arguments; // eslint-disable-line
        // eslint-disable-next-line
        const later = function () {
          timeout = null;
          if (!immediate) func.apply(context, args);
        };
        const callNow = immediate && !timeout;
        clearTimeout(timeout);
        timeout = setTimeout(later, wait);
        if (callNow) func.apply(context, args);
      };
    };
    // initialize debounced function for performance or else callback on every key will be expensive. looking at you IE
    this.updateParentDebounced = debounce(this.updateParent.bind(this), 500);
  }

  handleChange = e => {
    const { cell, colIdx, isOnSubmit } = this.props;
    const val = e.target.value;
    cell.active = false;
    this.setState({ value: val });
    if (!isOnSubmit) {
      this.updateParentDebounced(val, cell, 1, colIdx);
    }
  };

  updateParent = (val, cell, rowIdx, colIdx) => {
    const { i18n, onCellChange } = this.props;
    CybsScreenReaderManager.addNotification(
      i18n?.tableUpdated || FilterInput.defaultProps.i18n.tableUpdated
    );
    onCellChange(val, cell, rowIdx, colIdx);
  };

  applyFilter = () => {
    const { cell, colIdx, rowIdx } = this.props;
    const { value } = this.state;
    const updCell = { ...cell, active: true };
    this.updateParentDebounced(value, updCell, rowIdx, colIdx);
  };

  clearFilter = () => {
    const { cell, colIdx, rowIdx } = this.props;
    this.setState({ value: '' });
    this.updateParentDebounced('', cell, rowIdx, colIdx);
  };

  onCloseClick = () => {
    const { isOnSubmit } = this.props;

    if (!isOnSubmit) {
      this.clearFilter();
    } else {
      this.applyFilter();
    }
  };

  onPressEnter = () => {
    this.applyFilter();
  };

  onPressTab = e => {
    const { isOnSubmit } = this.props;
    if (!isOnSubmit) {
      e.preventDefault();
      this.applyFilter();
    }
  };

  getColorConfiguration = (theme, type) => {
    // used for alternate styling between active and inactive
    const { variables } = theme;
    const dic = {
      active: `
          
           .vds-input-bar, .vds-input--input-text {
               background-color: ${
                 theme &&
                 variables &&
                 variables.colors &&
                 variables.colors.action &&
                 variables.colors.action.primary.default
                   ? variables.colors.action.primary.default
                   : '#003ea9'
               } !important;
                color: ${
                  variables &&
                  variables.colors &&
                  variables.colors.misc &&
                  variables.colors.misc.white
                    ? variables.colors.misc.white
                    : '#fff'
                } !important;
           }
           background-color: ${
             theme &&
             variables &&
             variables.colors &&
             variables.colors.action &&
             variables.colors.action.primary.default
               ? variables.colors.action.primary.default
               : '#003ea9'
           };
        
      
      `,
      base: `
      .vds-input-bar {
        background-color: ${
          theme && theme.vds && theme.vds.inputBackground
            ? theme.vds.inputBackground
            : '#fff'
        };
      }
      `
    };

    return dic[type] ? dic[type] : '';
  };

  getInputStyle = theme => {
    let sty = `
        margin-top: 0 !important;
        min-width: 75px;
      `;

    sty += `${this.getColorConfiguration(theme, 'base')}`;

    return css`
      ${sty}
    `;
  };

  render() {
    const {
      colIdx,
      dataTest,
      i18n: i18nProp,
      id,
      isOnSubmit,
      label,
      rowIdx,
      width
    } = this.props;
    const i18n = {
      ...FilterInput.defaultProps.i18n,
      ...i18nProp
    };
    const { value } = this.state;
    // Tooltip label depends on isOnSubmit
    const tooltipLabel = isOnSubmit ? i18n.submitTooltip : i18n.clearTooltip;
    let iconClass = isOnSubmit ? 'arrow-forward' : 'close';
    iconClass = value ? iconClass : undefined;

    return (
      <CybsTextField
        activeButton={isOnSubmit}
        ariaLabel={label ? `${label} ${i18n.filter}` : `${rowIdx}, ${colIdx}`}
        css={theme => this.getInputStyle(theme)}
        dataTest={dataTest}
        i18n={{
          ...i18n,
          tooltipLabel,
          placeholder: i18n.helpTooltip || i18n.placeholder
        }}
        iconClass={iconClass}
        iconType="light-tiny"
        id={id}
        onChange={this.handleChange}
        onIconClick={this.onCloseClick}
        onKeyDown={e => {
          if (e.key === 'Enter') {
            e.preventDefault();
            this.onPressEnter();
          } else if (e.key === 'Tab' && value) {
            this.onPressTab(e);
          }
        }}
        resolution="tiny"
        value={value}
        width={width ? `${width}px` : '180px'}
        useNativePlaceholder
      />
    );
  }
}

FilterInput.displayName = 'Gridtable Filter Input';

FilterInput.propTypes = {
  colIdx: PropTypes.number.isRequired,
  cell: PropTypes.shape({ active: PropTypes.bool }).isRequired,
  dataTest: PropTypes.string.isRequired,
  i18n: PropTypes.shape({
    clearTooltip: PropTypes.string,
    helpTooltip: PropTypes.string,
    submitTooltip: PropTypes.string,
    filter: PropTypes.string,
    tableUpdated: PropTypes.string,
    placeholder: PropTypes.string
  }),
  id: PropTypes.string.isRequired,
  isOnSubmit: PropTypes.bool,
  label: PropTypes.string,
  onCellChange: PropTypes.func,
  rowIdx: PropTypes.number.isRequired,
  width: PropTypes.number,
  value: PropTypes.string
};

FilterInput.defaultProps = {
  i18n: {
    clearTooltip: 'Clear filter',
    helpTooltip: 'Press Enter to filter results',
    submitTooltip: 'Apply filter',
    filter: 'filter',
    tableUpdated: 'Table updated',
    placeholder: undefined
  },
  isOnSubmit: false,
  label: '',
  onCellChange: () => {},
  width: undefined,
  value: undefined
};

export default FilterInput;
